/**
 * LAP — Projection / Simulation épargne & cashflow.
 *
 * Onglet monté dans Mon Budget (consolidé) et dans le détail d'un compte.
 * Table mois par mois, chaque mois dépliable au niveau Classification → Catégorie
 * pour éditer les montants à cette granularité. Recalcul en direct du cashflow
 * et de l'épargne, cumulés à partir d'un solde de départ. Simulation 100% front.
 */
import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  CircularProgress,
  Collapse,
  Tooltip,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { makeGetRequest } from 'utils/api'
import urls from 'utils/urls'
import { useTranslation } from 'react-i18next'

const P = {
  bg: 'linear-gradient(180deg, #0a1024 0%, #0e1a36 100%)',
  card: 'rgba(255,255,255,0.035)',
  cardHover: 'rgba(255,255,255,0.06)',
  border: 'rgba(255,255,255,0.10)',
  borderStrong: 'rgba(126,156,219,0.30)',
  text: '#e8eefc',
  textDim: '#93a4c8',
  accent: '#7e9cdb',
  positive: '#4ade80',
  negative: '#f87171',
  warn: '#fbbf24',
  mono: '"Roboto Mono","SFMono-Regular","Courier New",monospace',
}

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
const chf = (n) => `${Number(n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
const sum = (arr) => arr.reduce((s, c) => s + (Number(c.amount) || 0), 0)

function monthLabel(offset) {
  const d = new Date()
  const idx = d.getMonth() + offset
  const m = ((idx % 12) + 12) % 12
  const y = d.getFullYear() + Math.floor(idx / 12)
  return `${MONTHS_FR[m]} ${String(y).slice(2)}`
}

function seedRev(seed) {
  if (Array.isArray(seed.revenus) && seed.revenus.length) {
    return seed.revenus.map((r) => ({ categorie: r.categorie || 'Revenus', amount: Number(r.amount) || 0 }))
  }
  return [{ categorie: 'Revenus', amount: Number(seed.avg_revenus) || 0 }]
}
function seedDep(seed) {
  if (Array.isArray(seed.depenses) && seed.depenses.length) {
    return seed.depenses.map((d) => ({ classification: d.classification || '', categorie: d.categorie || 'Dépenses', amount: Number(d.amount) || 0 }))
  }
  return [{ classification: '', categorie: 'Dépenses', amount: Number(seed.avg_depenses) || 0 }]
}
function buildMonth(seed, i) {
  // Échéances de contrats du mois (index 0 = mois prochain) — fournies par le
  // backend (BudgetOperations liées à un contrat), cycle de 12 mois.
  const contractsMonthly = (seed.contracts && seed.contracts.monthly) || []
  return {
    rev: seedRev(seed),
    dep: seedDep(seed),
    prov: Number(seed.provisions_mensuelles) || 0,
    contracts: Number(contractsMonthly[i % 12]) || 0,
  }
}
function cloneMonth(m) {
  return { rev: m.rev.map((c) => ({ ...c })), dep: m.dep.map((c) => ({ ...c })), prov: m.prov, contracts: m.contracts }
}
function buildRows(horizon, existing, seed) {
  const rows = []
  for (let i = 0; i < horizon; i++) rows.push(existing && existing[i] ? existing[i] : buildMonth(seed, i))
  return rows
}

function CumulativeChart({ epargne, cashflow }) {
  const W = 720
  const H = 150
  const pad = 8
  const all = [...epargne, ...cashflow, 0]
  const min = Math.min(...all)
  const max = Math.max(...all)
  const span = max - min || 1
  const n = epargne.length
  const x = (i) => pad + (n <= 1 ? 0 : (i * (W - 2 * pad)) / (n - 1))
  const y = (v) => H - pad - ((v - min) / span) * (H - 2 * pad)
  const toPath = (arr) => arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <line x1={pad} y1={y(0)} x2={W - pad} y2={y(0)} stroke="rgba(255,255,255,0.15)" strokeDasharray="4 4" />
        <path d={toPath(cashflow)} fill="none" stroke={P.accent} strokeWidth="2" opacity="0.85" />
        <path d={toPath(epargne)} fill="none" stroke={P.positive} strokeWidth="2.5" />
      </svg>
    </Box>
  )
}

const catInputSx = {
  '& .MuiOutlinedInput-root': { color: '#fff', fontFamily: P.mono, fontSize: 12 },
  '& .MuiOutlinedInput-input': { textAlign: 'right', py: 0.3, px: 0.6 },
  '.MuiOutlinedInput-notchedOutline': { borderColor: P.border },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: P.accent },
  width: 110,
}

/* ─────── Éditeur détaillé d'un mois (classification → catégorie) ─────── */
function MonthDetail({ month, onCat, onProv, onApplyAll, tr }) {
  const byClass = {}
  month.dep.forEach((d, idx) => {
    const k = d.classification || tr('other', 'Autre')
    ;(byClass[k] = byClass[k] || []).push({ ...d, idx })
  })

  return (
    <Box sx={{ bgcolor: 'rgba(0,0,0,0.25)', borderRadius: 1.5, p: 1.5, my: 0.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ color: P.textDim, fontSize: 11, fontStyle: 'italic' }}>
          {tr('detailHint', 'Ajustez les montants par catégorie ; les totaux et cumuls se recalculent.')}
        </Typography>
        <Tooltip title={tr('applyAllTip', 'Recopier ce mois sur tous les mois')}>
          <Button size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />} onClick={onApplyAll} sx={{ color: P.accent, textTransform: 'none', fontSize: 11 }}>
            {tr('applyAll', 'Appliquer à tous')}
          </Button>
        </Tooltip>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Revenus */}
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ color: P.positive, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>
            {tr('revenus', 'Revenus')}
          </Typography>
          {month.rev.map((c, idx) => (
            <Stack key={`${c.categorie}-${idx}`} direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 0.3 }}>
              <Typography sx={{ color: P.text, fontSize: 12.5, flex: 1 }}>{c.categorie}</Typography>
              <TextField size="small" type="number" value={c.amount} onChange={(e) => onCat('rev', idx, e.target.value)} sx={catInputSx} />
            </Stack>
          ))}
        </Box>

        {/* Dépenses par classification */}
        <Box sx={{ flex: 1.4, minWidth: 280 }}>
          <Typography sx={{ color: P.negative, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>
            {tr('depenses', 'Dépenses')}
          </Typography>
          {Object.keys(byClass).map((cls) => (
            <Box key={cls} sx={{ mb: 0.5 }}>
              <Typography sx={{ color: P.accent, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', borderBottom: `1px solid ${P.border}`, mb: 0.3 }}>
                {cls}
              </Typography>
              {byClass[cls].map((c) => (
                <Stack key={`${c.categorie}-${c.idx}`} direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 0.3, pl: 1 }}>
                  <Typography sx={{ color: P.text, fontSize: 12.5, flex: 1 }}>{c.categorie}</Typography>
                  <TextField size="small" type="number" value={c.amount} onChange={(e) => onCat('dep', c.idx, e.target.value)} sx={catInputSx} />
                </Stack>
              ))}
            </Box>
          ))}

          {/* Contrats — lecture seule : calendrier réel des échéances */}
          {Number(month.contracts) > 0 && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 0.3, mt: 0.5, borderTop: `1px solid ${P.border}` }}>
              <Tooltip title={tr('contractsTip', 'Échéances des contrats liés — gérées dans le module Contrats, non éditables ici.')}>
                <Typography sx={{ color: P.accent, fontSize: 12.5, flex: 1, fontWeight: 600 }}>{tr('contracts', 'Contrats')}</Typography>
              </Tooltip>
              <Typography sx={{ color: P.accent, fontFamily: P.mono, fontSize: 12, width: 110, textAlign: 'right', pr: 0.6 }}>
                {chf(month.contracts)}
              </Typography>
            </Stack>
          )}

          {/* Provisions */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 0.3, mt: 0.5, borderTop: `1px solid ${P.border}` }}>
            <Typography sx={{ color: P.warn, fontSize: 12.5, flex: 1, fontWeight: 600 }}>{tr('provisions', 'Provisions')}</Typography>
            <TextField size="small" type="number" value={month.prov} onChange={(e) => onProv(e.target.value)} sx={catInputSx} />
          </Stack>
        </Box>
      </Stack>
    </Box>
  )
}

export default function Projection({ accountId = null }) {
  const { t } = useTranslation('layout')
  const tr = (k, dflt) => t(`finance.projection.${k}`, dflt)

  const [horizon, setHorizon] = useState(12)
  const [seed, setSeed] = useState(null)
  const [rows, setRows] = useState([])
  const [startingBalance, setStartingBalance] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())

  const fetchSeed = useCallback(async () => {
    setLoading(true)
    let url = urls.financeProjectionSeed
    if (accountId) url += `?account=${accountId}`
    const { data: res, status } = await makeGetRequest(url, {}, true)
    if (status === 200 && res) {
      setSeed(res)
      setStartingBalance(res.starting_balance || 0)
      setRows(buildRows(horizon, null, res))
    }
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId])

  useEffect(() => {
    fetchSeed()
  }, [fetchSeed])

  useEffect(() => {
    if (seed) setRows((prev) => buildRows(horizon, prev, seed))
  }, [horizon, seed])

  const toggle = (i) =>
    setExpanded((prev) => {
      const n = new Set(prev)
      if (n.has(i)) n.delete(i)
      else n.add(i)
      return n
    })

  const setCat = (mi, side, idx, value) => {
    const v = Number(value)
    setRows((prev) =>
      prev.map((m, i) => {
        if (i !== mi) return m
        const arr = m[side].map((c, ci) => (ci === idx ? { ...c, amount: Number.isNaN(v) ? 0 : v } : c))
        return { ...m, [side]: arr }
      }),
    )
  }
  const setProv = (mi, value) => {
    const v = Number(value)
    setRows((prev) => prev.map((m, i) => (i === mi ? { ...m, prov: Number.isNaN(v) ? 0 : v } : m)))
  }
  const applyAll = (mi) => {
    setRows((prev) => {
      const src = prev[mi]
      // Les échéances de contrats restent propres à chaque mois (calendrier réel).
      return prev.map((m) => ({ ...cloneMonth(src), contracts: m.contracts }))
    })
  }
  const reset = () => {
    if (seed) {
      setRows(buildRows(horizon, null, seed))
      setStartingBalance(seed.starting_balance || 0)
      setExpanded(new Set())
    }
  }

  // Ligne « Contrats » affichée seulement si des contrats alimentent le budget.
  const hasContracts = Boolean(seed && seed.contracts && seed.contracts.count > 0)
  const colCount = hasContracts ? 9 : 8

  // Cumuls.
  let cumE = Number(startingBalance) || 0
  let cumC = Number(startingBalance) || 0
  const computed = rows.map((m) => {
    const revTotal = sum(m.rev)
    const depTotal = sum(m.dep)
    const contracts = Number(m.contracts) || 0
    const cashflow = revTotal - depTotal - contracts
    const epargne = cashflow - (Number(m.prov) || 0)
    cumC += cashflow
    cumE += epargne
    return { revTotal, depTotal, contracts, cashflow, epargne, cumCashflow: cumC, cumEpargne: cumE }
  })
  const finalE = computed.length ? computed[computed.length - 1].cumEpargne : Number(startingBalance) || 0
  const finalC = computed.length ? computed[computed.length - 1].cumCashflow : Number(startingBalance) || 0

  return (
    <Box sx={{ background: P.bg, borderRadius: 2, p: { xs: 1.5, sm: 2.5 }, color: P.text }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ color: P.text, fontWeight: 700, fontSize: 17 }}>{tr('title', 'Projection & simulation')}</Typography>
          <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>
            {tr('subtitle', 'Dépliez un mois pour ajuster par classification/catégorie ; épargne et cashflow cumulés en direct.')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={horizon} onChange={(e, v) => v && setHorizon(v)}
            sx={{ '& .MuiToggleButton-root': { color: P.textDim, borderColor: P.border, px: 1.5 }, '& .Mui-selected': { color: '#fff !important', bgcolor: 'rgba(126,156,219,0.35) !important' } }}>
            <ToggleButton value={12}>12 {tr('months', 'mois')}</ToggleButton>
            <ToggleButton value={24}>24</ToggleButton>
            <ToggleButton value={36}>36</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" onClick={reset} startIcon={<RestartAltIcon />} variant="outlined" sx={{ color: P.textDim, borderColor: P.border, textTransform: 'none' }}>
            {tr('reset', 'Réinitialiser')}
          </Button>
        </Stack>
      </Stack>

      {loading || !seed ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} sx={{ color: P.accent }} />
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: 180, bgcolor: P.card, border: `1px solid ${P.border}`, borderRadius: 2, p: 1.5 }}>
              <Typography sx={{ color: P.textDim, fontSize: 11 }}>{tr('startingBalance', 'Solde de départ')}</Typography>
              <TextField variant="standard" type="number" value={startingBalance} onChange={(e) => setStartingBalance(Number(e.target.value) || 0)}
                InputProps={{ sx: { color: '#fff', fontFamily: P.mono, fontSize: 18, fontWeight: 700, mt: 0.3 } }} />
            </Box>
            <Kpi label={tr('finalEpargne', 'Épargne cumulée (fin)')} value={finalE} color={finalE >= 0 ? P.positive : P.negative} highlight />
            <Kpi label={tr('finalCashflow', 'Cashflow cumulé (fin)')} value={finalC} color={finalC >= 0 ? P.positive : P.accent} />
          </Stack>

          <Box sx={{ bgcolor: P.card, border: `1px solid ${P.border}`, borderRadius: 2, p: 1.5, mb: 1.5 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 0.5 }}>
              <Legend color={P.positive} label={tr('cumEpargne', 'Épargne cumulée')} />
              <Legend color={P.accent} label={tr('cumCashflow', 'Cashflow cumulé')} />
            </Stack>
            <CumulativeChart epargne={computed.map((r) => r.cumEpargne)} cashflow={computed.map((r) => r.cumCashflow)} />
          </Box>

          <Box sx={{ bgcolor: P.card, border: `1px solid ${P.border}`, borderRadius: 2, p: 1, overflowX: 'auto' }}>
            <Box component="table" sx={tableSx}>
              <thead>
                <tr>
                  <th>{tr('month', 'Mois')}</th>
                  <th>{tr('revenus', 'Revenus')}</th>
                  <th>{tr('depenses', 'Dépenses')}</th>
                  {hasContracts && <th>{tr('contracts', 'Contrats')}</th>}
                  <th>{tr('provisions', 'Provisions')}</th>
                  <th>{tr('cashflow', 'Cashflow')}</th>
                  <th>{tr('epargne', 'Épargne')}</th>
                  <th>{tr('cumEpargne', 'Épargne cumul.')}</th>
                  <th>{tr('cumCashflow', 'Cashflow cumul.')}</th>
                </tr>
              </thead>
              <tbody>
                {computed.map((c, i) => {
                  const open = expanded.has(i)
                  return (
                    <React.Fragment key={i}>
                      <tr onClick={() => toggle(i)} style={{ cursor: 'pointer' }}>
                        <td style={{ whiteSpace: 'nowrap', color: P.textDim }}>
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            {open ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
                            <span>{monthLabel(i + 1)}</span>
                          </Stack>
                        </td>
                        <td style={{ color: P.positive }}>{chf(c.revTotal)}</td>
                        <td style={{ color: P.negative }}>{chf(c.depTotal)}</td>
                        {hasContracts && <td style={{ color: P.accent }}>{chf(c.contracts)}</td>}
                        <td style={{ color: P.warn }}>{chf(rows[i].prov)}</td>
                        <td style={{ color: c.cashflow >= 0 ? P.positive : P.negative }}>{chf(c.cashflow)}</td>
                        <td style={{ color: c.epargne >= 0 ? P.positive : P.negative }}>{chf(c.epargne)}</td>
                        <td style={{ color: c.cumEpargne >= 0 ? P.positive : P.negative, fontWeight: 700 }}>{chf(c.cumEpargne)}</td>
                        <td style={{ color: c.cumCashflow >= 0 ? P.positive : P.accent }}>{chf(c.cumCashflow)}</td>
                      </tr>
                      <tr>
                        <td colSpan={colCount} style={{ padding: 0, borderBottom: 'none' }}>
                          <Collapse in={open} unmountOnExit>
                            <MonthDetail
                              month={rows[i]}
                              tr={tr}
                              onCat={(side, idx, val) => setCat(i, side, idx, val)}
                              onProv={(val) => setProv(i, val)}
                              onApplyAll={() => applyAll(i)}
                            />
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })}
              </tbody>
            </Box>
          </Box>
          <Typography sx={{ color: P.textDim, fontSize: 11, mt: 1, fontStyle: 'italic' }}>
            {hasContracts
              ? tr('hintContracts', 'Valeurs initiales = moyenne des 12 derniers mois (virements internes exclus) ; la colonne Contrats suit le calendrier réel des échéances (moyennes ajustées pour éviter le double comptage). Montants en CHF.')
              : tr('hint', 'Valeurs initiales = moyenne des 12 derniers mois (virements internes exclus). Montants en CHF.')}
          </Typography>
        </>
      )}
    </Box>
  )
}

function Kpi({ label, value, color, highlight }) {
  return (
    <Box sx={{ flex: '1 1 180px', minWidth: 170, bgcolor: highlight ? 'rgba(126,156,219,0.12)' : P.card, border: `1px solid ${highlight ? P.borderStrong : P.border}`, borderRadius: 2, p: 1.5 }}>
      <Typography sx={{ color: P.textDim, fontSize: 11 }}>{label}</Typography>
      <Typography sx={{ color, fontSize: 18, fontWeight: 700, fontFamily: P.mono, mt: 0.4 }}>{chf(value)} CHF</Typography>
    </Box>
  )
}

function Legend({ color, label }) {
  return (
    <Stack direction="row" spacing={0.6} alignItems="center">
      <Box sx={{ width: 14, height: 3, borderRadius: 2, bgcolor: color }} />
      <Typography sx={{ color: P.textDim, fontSize: 11 }}>{label}</Typography>
    </Stack>
  )
}

const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: P.mono,
  fontSize: 12.5,
  '& th': { textAlign: 'right', color: P.textDim, fontWeight: 600, padding: '6px 8px', borderBottom: `1px solid ${P.border}`, whiteSpace: 'nowrap' },
  '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
  '& tbody td': { textAlign: 'right', color: P.text, padding: '4px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
}
